/**
 * Spelerspecifieke weergave.
 *
 * De server stuurt nooit de volledige spelstand naar een client: handkaarten en de
 * inhoud van de stapels zijn geheim. `viewFor` maakt daar één plek van — wat hier niet
 * in zit, komt niet over de lijn. Verbergen in de UI is geen bescherming (plan §2).
 */
import type { Seat } from '@catan/contracts';
import { CARD_STATS, pointsOf, soloScore, type Action } from './game.js';
import { tournamentOf } from './turn.js';
import { commercePower, knightPower } from './tokens.js';
import type {
  DiceState,
  GameState,
  OpenOffer,
  Pending,
  Phase,
  Principality,
  Side,
  StartLandschap,
} from './state.js';
import { opponentOf } from './state.js';
import { legalActions } from './zetten.js';

export interface PlayerView {
  readonly seat: Seat;
  readonly name: string;
  readonly principality: Principality;
  readonly handLimit: number;
  /**
   * De drie machten die de statusbalk toont.
   *
   * Ze staan op de kaarten die je gebouwd hebt: toernooipunten op de ridders, handelspunten
   * op de vloten en markten, sterkte op de ridders. Ze stonden er nog niet in en de balk
   * toonde daardoor altijd een nul bij toernooi — terwijl het riddertoernooi juist op dat
   * getal beslist.
   */
  readonly tournamentPoints: number;
  readonly commercePoints: number;
  readonly strength: number;
  /**
   * Overwinningspunten, inclusief de twee speelfiguren.
   *
   * `pointsOf` heeft de hele spelstand nodig — de figuren hangen niet aan één speler —
   * dus het scherm kan dit niet zelf uitrekenen zodra het alleen nog een weergave heeft.
   */
  readonly victoryPoints: number;
}

/** Jouw eigen kant: je ziet je kaarten. */
export interface SelfView extends PlayerView {
  readonly hand: readonly number[];
}

/** De overkant: je ziet alleen hoevéél kaarten er zijn. */
export interface OpponentView extends PlayerView {
  readonly handCount: number;
  /**
   * Zijn kaarten — maar alleen op het moment dat je ze mag zien.
   *
   * Dat is bij de **Spion** ("Kies uit de handkaarten van je tegenstander 1 kaart uit") en
   * bij **Conflict** ("kiest uit de handkaarten van de tegenstander 2 kaarten uit"). Twee
   * keer bijna dezelfde zin, dus twee keer hetzelfde antwoord: kiezen kan alleen als je
   * ziet waaruit. Buiten die twee momenten staat hier niets.
   */
  readonly hand: readonly number[] | undefined;
}

export interface GameView {
  readonly rulesVersion: number;
  readonly cardsVersion: number;
  readonly you: SelfView;
  readonly opponent: OpponentView;
  readonly turn: Seat;
  readonly turnNumber: number;
  readonly phase: Phase;
  readonly dice: DiceState | undefined;
  /**
   * Hoe dik elke uitbouwstapel nog is. De inhoud niet: die ligt dicht.
   *
   * "Het pakken van de bovenste kaart van een stapel is gratis. Maar ook bij het ruilen
   * van een kaart kun je tegen betaling van 2 grondstoffen een hele stapel bekijken en
   * een willekeurige kaart uitkiezen" (handboek p. 15). Je betaalt dus voor het kíjken —
   * en dat is precies waarom de inhoud hier niet zomaar in staat.
   */
  readonly stackSizes: readonly number[];
  /**
   * De ene stapel die je nu wél mag inzien, als je ervoor betaald hebt.
   *
   * Zolang die vraag openstaat mag je hem doorbladeren; daarna gaat hij weer dicht. Zo
   * blijft het voordeel bij degene die ervoor betaalde.
   */
  readonly openStapel: { readonly stack: number; readonly kaarten: readonly number[] } | undefined;
  /**
   * De gebeurtenisstapel blijft dicht: alleen hoe dik hij nog is.
   *
   * Dat is het verschil dat ertoe doet — wat er bovenop ligt, mag niemand weten.
   */
  readonly eventDeckSize: number;
  readonly discard: readonly number[];
  readonly knightToken: Seat | undefined;
  readonly windmillToken: Seat | undefined;
  readonly winner: Seat | undefined;
  /** Ben jij aan zet? */
  readonly yourTurn: boolean;

  /**
   * De vraag waar het spel op wacht, van wie hij ook is.
   *
   * Niet geheim: dat de ander een kaart moet afleggen mag je zien, en je moet het zelfs
   * zien — anders lijkt het scherm te hangen. Wélke kaart hij kiest blijft verborgen,
   * want een `pending` draagt hoogstens een index, nooit een kaart.
   */
  readonly pending: Pending | undefined;
  /** Een ruilaanbod dat op antwoord wacht. Openbaar: het gaat tussen jullie tweeën. */
  readonly offer: OpenOffer | undefined;
  /** De gebeurteniskaart die zojuist getrokken is; het scherm toont hem groot. */
  readonly lastEventCard: number | undefined;
  /** Jouw eigen landschappen die nog een plek moeten krijgen. Die van de ander niet. */
  readonly setupPool: readonly StartLandschap[];
  /** Hoeveel landschappen er nog op de stapel liggen. */
  readonly landscapeDeckSize: number;
  /**
   * De landschapstapel zelf — alleen als jíj hem mag inzien.
   *
   * Dat mag met de **Verkenner**, en alleen dan (handboek p. 46). Hem altijd meesturen
   * zou verklappen wat het volgende dorp oplevert, want er wordt van boven af getrokken.
   */
  readonly landscapeDeck: readonly StartLandschap[] | undefined;
  /**
   * Wat jij nu mag doen.
   *
   * Het scherm rekende dit vroeger zelf uit, en had daarvoor de volledige spelstand
   * nodig — inclusief de hand van je tegenstander. Dat kan niet over de lijn. Nu rekent
   * de kant die alles weet het uit, en krijg je alleen de uitkomst.
   *
   * Er staat geen ruilaanbod in: wát je aanbiedt stel je zelf samen. Zie `zetten.ts`.
   */
  readonly legalActions: readonly Action[];
  /**
   * De score van een oefenpotje, zoals het eindscherm hem toont.
   *
   * Honderd plus honderd per punt verschil, plus het verschil in geoogste grondstoffen
   * (handboek p. 8). Ook dit heeft de hele stand nodig.
   */
  readonly soloScore: number;
}

/** De machten van een speler, opgeteld uit de kaarten in zijn vorstendom. */
function machten(player: GameState['players'][number]): {
  tournamentPoints: number;
  commercePoints: number;
  strength: number;
} {
  return {
    tournamentPoints: tournamentOf(player, CARD_STATS),
    // De getallen die de statusbalk toont horen dezelfde te zijn als die over de
    // speelfiguren beslissen. Hier stond `commerceOf`/`strengthOf`: de kále sommen, zonder
    // de bonussen van de Haven en de Smederij. Je bouwde een Smederij, je riddermacht steeg
    // echt — de figuur kon zelfs van eigenaar wisselen — maar het getal in de balk bewoog
    // niet. Uit de eerste live test: "de speler zag zijn ridders niet in waarde stijgen".
    commercePoints: commercePower(player),
    strength: knightPower(player),
  };
}

/**
 * De openstaande vraag, zonder wat er niet bij hoort.
 *
 * `pending` gaat naar béíde spelers, en dat hoort ook: dat de ander een kaart moet afleggen
 * mag je zien, anders lijkt het scherm te hangen. Maar twee varianten dragen een kaart-id
 * mee — `kies-afleg-stapel` welke kaart je wegschuift, `kies-tegenkaart` welk verweer je in
 * je hand hebt — en dat is precies wat de ander níét mag weten. De docstring bij
 * `GameView.pending` beloofde "hoogstens een index, nooit een kaart"; hier wordt dat waar
 * gemaakt.
 *
 * Voor de eigenaar van de vraag verandert er niets: die kent zijn eigen kaarten al.
 */
function pendingVoor(pending: Pending | undefined, seat: Seat): Pending | undefined {
  if (pending === undefined || pending.seat === seat) return pending;
  if (pending.kind === 'kies-afleg-stapel') return { ...pending, cardId: -1 };
  if (pending.kind === 'kies-tegenkaart') return { ...pending, counterId: -1 };
  return pending;
}

export function viewFor(state: GameState, seat: Seat): GameView {
  const you = state.players[seat];
  const other = state.players[opponentOf(seat)];

  return {
    rulesVersion: state.rulesVersion,
    cardsVersion: state.cardsVersion,
    you: {
      seat: you.seat,
      name: you.name,
      principality: you.principality,
      handLimit: you.handLimit,
      ...machten(you),
      victoryPoints: pointsOf(state, seat),
      hand: you.hand,
    },
    opponent: {
      seat: other.seat,
      name: other.name,
      principality: other.principality,
      handLimit: other.handLimit,
      ...machten(other),
      victoryPoints: pointsOf(state, other.seat),
      handCount: other.hand.length,
      hand: magHandInzien(state, seat) ? other.hand : undefined,
    },
    turn: state.turn,
    turnNumber: state.turnNumber,
    phase: state.phase,
    dice: state.dice,
    stackSizes: state.stacks.map((stapel) => stapel.length),
    openStapel: openStapelVoor(state, seat),
    eventDeckSize: state.eventDeck.length,
    // De aflegstapel ligt open op tafel.
    discard: state.discard,
    knightToken: state.knightToken,
    windmillToken: state.windmillToken,
    winner: state.winner,
    yourTurn: state.turn === seat,
    pending: pendingVoor(state.pending, seat),
    offer: state.offer,
    lastEventCard: state.lastEventCard,
    setupPool: state.setupPool[seat],
    landscapeDeckSize: state.landscapeDeck.length,
    landscapeDeck: magStapelInzien(state, seat) ? state.landscapeDeck : undefined,
    legalActions: legalActions(state, seat),
    soloScore: soloScore(state, seat),
  };
}

/**
 * Mag deze speler nu de hand van zijn tegenstander zien?
 *
 * Alleen zolang hij er iets uit moet kiezen: de Spion of Conflict. Daarbuiten nooit — ook
 * niet als je hem net nog zag.
 */
function magHandInzien(state: GameState, seat: Seat): boolean {
  const pending = state.pending;
  if (pending === undefined || pending.seat !== seat) return false;
  return pending.kind === 'spion' || pending.kind === 'kies-handkaarten';
}

/** Mag deze speler de landschapstapel inzien? Alleen met de Verkenner in de hand. */
function magStapelInzien(state: GameState, seat: Seat): boolean {
  return state.pending?.kind === 'verkenner-kies' && state.pending.seat === seat;
}

/**
 * Welke uitbouwstapel deze speler op dit moment mag doorbladeren.
 *
 * Drie momenten geven dat recht: je startkaarten kiezen, een stapel doorkopen voor twee
 * grondstoffen, en de Bouwmeester zodra je je stapel hebt aangewezen. Buiten die momenten
 * ziet niemand de inhoud — ook zijn eigen stapels niet, want ze zijn gedeeld.
 */
function openStapelVoor(
  state: GameState,
  seat: Seat,
): { stack: number; kaarten: readonly number[] } | undefined {
  const pending = state.pending;
  if (pending === undefined || pending.seat !== seat) return undefined;

  const stack =
    pending.kind === 'kies-startkaarten' || pending.kind === 'kies-uit-stapel'
      ? pending.stack
      : pending.kind === 'bouwmeester' && pending.stap === 'kaart'
        ? pending.stack
        : undefined;
  if (stack === undefined) return undefined;

  const kaarten = state.stacks[stack];
  return kaarten === undefined ? undefined : { stack, kaarten };
}

/**
 * Een zet zoals de tegenstander hem mag zien.
 *
 * De client heeft maar zes velden nodig om een zet af te spelen — `naZet` in de webapp
 * leest er nooit meer uit. De rest (`payment`, `target`, `handIndex`, `stack`, `give`,
 * `want`) gaat er dus niet in.
 *
 * Bewust een **witte lijst van velden** en geen zwarte lijst van actietypes: de
 * `Action`-union heeft twintig varianten en groeit. Een zwarte lijst faalt open zodra
 * iemand er een variant bij zet; deze faalt dicht — een nieuwe variant lekt hoogstens
 * deze zes.
 */
export interface ZichtbareActie {
  readonly type: Action['type'];
  readonly column?: number;
  readonly side?: Side;
  readonly cardId?: number;
  readonly accepteren?: boolean;
  readonly inzetten?: boolean;
}

/**
 * De pendings waarbij `{type:'kies', column, side}` een echte plek op tafel aanwijst.
 *
 * Overal elders draagt `column` een index in iets wat de ander niet mag zien: een plaats
 * in een hand (`spion`, `kies-handkaarten`, `kies-afleggen`), in een stapel
 * (`kies-uit-stapel`, `kies-startkaarten`, `verkenner-kies`, `bouwmeester`) of de stapel
 * waaronder je iets wegschoof (`kies-afleg-stapel`). Dat getal doorgeven is geen detail:
 * met `handCount` en de zichtbare aflegstapel erbij is het het begin van kaarten tellen.
 *
 * `kies-terugname` staat er wél in — een gebouwde ridder ligt open op tafel.
 */
const KIES_WIJST_NAAR_TAFEL: ReadonlySet<Pending['kind']> = new Set([
  'kies-opbrengst',
  'kies-buit',
  'plaats-buit',
  'handelaar',
  'handelaar-terug',
  'karavaan',
  'kies-terugname',
  'leg-landschap',
]);

/**
 * Wat de tegenstander van deze zet te zien krijgt.
 *
 * Let op de volgorde bij de aanroeper: dit moet met de stand van **vóór** `perform`,
 * want alleen dan zegt `state.pending` nog waar een `kies` naar wees.
 */
export function zichtbareActie(state: GameState, actie: Action): ZichtbareActie {
  const uit: {
    type: Action['type'];
    column?: number;
    side?: Side;
    cardId?: number;
    accepteren?: boolean;
    inzetten?: boolean;
  } = { type: actie.type };

  // De ingezette tegenkaart is op dít moment openbaar: het verslag noemt hem bij naam en
  // hij gaat open op de aflegstapel. Zonder het id kan de aanvaller de Kruidenheks of
  // Bisschop niet groot in beeld krijgen — dan blijft het verweer een tekstregel. Vóór het
  // antwoord blijft het id geheim (`pendingVoor` maskeert), en een tegenkaart die níét
  // wordt ingezet blijft dat ook: die zit nog gewoon verborgen in de hand.
  if (actie.type === 'tegenkaart' && actie.inzetten && state.pending?.kind === 'kies-tegenkaart') {
    uit.cardId = state.pending.counterId;
  }

  if (actie.type === 'kies') {
    const soort = state.pending?.kind;
    if (soort !== undefined && KIES_WIJST_NAAR_TAFEL.has(soort)) {
      uit.column = actie.column;
      uit.side = actie.side;
    }
    return uit;
  }

  if ('column' in actie && typeof actie.column === 'number') uit.column = actie.column;
  if ('side' in actie && actie.side !== undefined) uit.side = actie.side;
  if ('cardId' in actie && typeof actie.cardId === 'number') uit.cardId = actie.cardId;
  if ('accepteren' in actie) uit.accepteren = actie.accepteren;
  if ('inzetten' in actie) uit.inzetten = actie.inzetten;
  return uit;
}
